import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { twMerge } from 'tailwind-merge'

interface CarouselProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode[]
  itemsPerPage: number
  autoplayDelay?: number
  spaceBetween?: number
  navigation?: boolean
  marginRight?: number
  marginLeft?: number
  carouselInsideButtons?: boolean
  allItemsFit?: boolean
  centerItems?: boolean
}

export function Carousel({
  children,
  itemsPerPage,
  autoplayDelay,
  spaceBetween = 0,
  navigation = true,
  marginRight = 8,
  marginLeft = 8,
  className,
  carouselInsideButtons = false,
  allItemsFit = false,
  centerItems = false,
}: CarouselProps) {
  const [currentPage, setCurrentPage] = useState(0)
  const totalPages = Math.ceil(children.length / itemsPerPage)

  const autoplayRef = useRef<number | null>(null)
  const isUserInteractingRef = useRef(false)

  const isPageEmpty = useCallback(
    (page: number) => {
      const startIndex = page * itemsPerPage
      const endIndex = startIndex + itemsPerPage
      return children.slice(startIndex, endIndex).length === 0
    },
    [children, itemsPerPage],
  )

  const goToNextPage = useCallback(() => {
    setCurrentPage((prevPage) => {
      const nextPage = (prevPage + 1) % totalPages
      if (isPageEmpty(nextPage)) {
        return prevPage
      }
      return nextPage
    })
  }, [isPageEmpty, totalPages])

  const goToPrevPage = () => {
    setCurrentPage((prevPage) => {
      const prevPageIndex = (prevPage - 1 + totalPages) % totalPages
      if (isPageEmpty(prevPageIndex)) {
        return prevPage
      }
      return prevPageIndex
    })
  }

  // Vai reiniciar o autoPlay
  const startAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current)
    }

    if (autoplayDelay) {
      autoplayRef.current = window.setInterval(() => {
        if (!isUserInteractingRef.current) {
          goToNextPage()
        }
      }, autoplayDelay)
    }
  }, [autoplayDelay, goToNextPage])

  // Função para pausar o autoPlay temporariamente
  function stopAutoplayTemporarily() {
    isUserInteractingRef.current = true

    if (autoplayRef.current) {
      clearInterval(autoplayRef.current)
    }

    setTimeout(() => {
      isUserInteractingRef.current = false
      startAutoplay()
    }, autoplayDelay)
  }

  useEffect(() => {
    startAutoplay()

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current)
      }
    }
  }, [startAutoplay])

  useEffect(() => {
    // Verifica se a página atual ficou vazia após a mudança na lista de imagens
    if (itemsPerPage > 1) {
      if (isPageEmpty(currentPage)) {
        setCurrentPage((prevPage) => {
          const value = (prevPage - 1 + totalPages) % totalPages
          return value || 0
        })
      }
    }
  }, [currentPage, isPageEmpty, itemsPerPage, totalPages])

  const getItemWidth = () => {
    return `calc((100% / ${itemsPerPage}) - ${spaceBetween * ((itemsPerPage - 1) / itemsPerPage)}px)`
  }

  const getTranslateX = () => {
    return `translateX(calc(-${currentPage * 100}% - ${currentPage * spaceBetween}px))`
  }

  return (
    <Wrapper carouselInsideButtons={carouselInsideButtons}>
      <div
        className={twMerge(
          'flex transition-transform duration-700 ease-in-out',
          allItemsFit && 'justify-center',
        )}
        style={{
          transform: getTranslateX(),
          gap: `${spaceBetween}px`,
        }}
      >
        {children.map((item, index) => (
          <div
            key={index}
            className={twMerge(
              'shrink-0',
              centerItems && 'flex justify-center',
            )}
            style={{
              width: getItemWidth(),
            }}
          >
            {item}
          </div>
        ))}
      </div>

      {itemsPerPage < children.length && (
        <>
          {/* Botão para voltar */}
          <div
            className={twMerge(
              'absolute top-1/2 -translate-y-1/2 transform',
              carouselInsideButtons && 'pb-4',
            )}
            style={{ left: carouselInsideButtons ? 0 : marginLeft }}
          >
            <button
              type="button"
              onClick={() => {
                goToPrevPage()
                if (autoplayDelay) {
                  stopAutoplayTemporarily()
                }
              }}
              className={twMerge(
                'cursor-pointer rounded-full bg-white p-1 transition disabled:opacity-60',
                className,
                carouselInsideButtons && 'disabled:hidden',
              )}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="text-bios-primary size-6 2xl:size-8" />
            </button>
          </div>

          {/* Botão para avançar */}
          <div
            className={twMerge(
              'absolute top-1/2 -translate-y-1/2 transform',
              carouselInsideButtons && 'pb-4',
            )}
            style={{ right: carouselInsideButtons ? 0 : marginRight }}
          >
            <button
              type="button"
              onClick={() => {
                goToNextPage()
                if (autoplayDelay) {
                  stopAutoplayTemporarily()
                }
              }}
              className={twMerge(
                'cursor-pointer rounded-full bg-white p-1 transition disabled:opacity-60',
                className,
                carouselInsideButtons && 'disabled:hidden',
              )}
              disabled={currentPage === totalPages - 1}
            >
              <ChevronRight className="text-bios-primary size-6 2xl:size-8" />
            </button>
          </div>

          {/* Indicador de Paginação */}
          {navigation && (
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 space-x-2">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  type="button"
                  key={index}
                  onClick={() => {
                    setCurrentPage(index)
                    if (autoplayDelay) {
                      stopAutoplayTemporarily()
                    }
                  }}
                  className={`size-2 rounded-full ${
                    currentPage === index ? 'bg-bios-primary' : 'bg-black/20'
                  }`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </Wrapper>
  )
}

interface WrapperProps {
  children: ReactNode
  carouselInsideButtons: boolean
}

const Wrapper = ({ children, carouselInsideButtons }: WrapperProps) => {
  return carouselInsideButtons ? (
    <div className="relative px-12">
      <div className="w-full overflow-hidden bg-transparent pb-8">
        {children}
      </div>
    </div>
  ) : (
    <div className="relative w-full overflow-hidden bg-transparent">
      {children}
    </div>
  )
}
